import React from 'react';
import {
  HeartPulse, TrendingUp, Newspaper, Shirt, Scale, Church, Users, Cpu,
  GraduationCap, Leaf, ChevronLeft, ChevronRight, Play, Square, RotateCcw,
} from 'lucide-react';
import { useStore } from '../store';
import { ZONES } from '../config/zones';

const ICONS = { HeartPulse, TrendingUp, Newspaper, Shirt, Scale, Church, Users, Cpu, GraduationCap, Leaf };

export default function TourPanel() {
  const currentZone = useStore((s) => s.currentZone);
  const tourActive = useStore((s) => s.tourActive);
  const bankFocus = useStore((s) => s.bankFocus);
  const startTour = useStore((s) => s.startTour);
  const stopTour = useStore((s) => s.stopTour);
  const nextZone = useStore((s) => s.nextZone);
  const prevZone = useStore((s) => s.prevZone);
  const replayZone = useStore((s) => s.replayZone);
  const narrateBank = useStore((s) => s.narrateBank);
  const narrateCompare = useStore((s) => s.narrateCompare);

  const zone = ZONES[currentZone];
  const Icon = ICONS[zone.icon] || Leaf;
  const progress = ((currentZone + 1) / ZONES.length) * 100;

  return (
    <div data-testid="tour-panel" className="glass-gold w-[19rem] p-6 flex flex-col gap-5 pointer-events-auto animate-hud-in">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
          Zone {String(zone.number).padStart(2, '0')} / {ZONES.length}
        </span>
        {tourActive && (
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse-gold" /> Touring
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mt-2">
        <div className="p-2.5 border border-gold/30 text-gold">
          <Icon className="w-6 h-6" strokeWidth={1.3} />
        </div>
        <h3 className="font-serif font-medium text-white text-3xl leading-tight">{zone.title}</h3>
      </div>

      {/* Bank focus — click to hear just that bank's briefing (glows in its colour) */}
      <div className="flex gap-2 text-[10px] font-mono uppercase tracking-widest">
        <button data-testid="briefing-liberal-btn" onClick={() => narrateBank('progressive')}
          className={`flex-1 px-2 py-1.5 border text-center transition-colors duration-300 pointer-events-auto hover:border-[#3B82F6] hover:text-[#3B82F6] ${bankFocus === 'progressive' ? 'border-[#3B82F6] text-[#3B82F6] bg-[#3B82F6]/15 animate-pulse-gold' : 'border-white/10 text-white/40'}`}>
          Liberal
        </button>
        <button data-testid="briefing-conservative-btn" onClick={() => narrateBank('traditional')}
          className={`flex-1 px-2 py-1.5 border text-center transition-colors duration-300 pointer-events-auto hover:border-[#EF4444] hover:text-[#EF4444] ${bankFocus === 'traditional' ? 'border-[#EF4444] text-[#EF4444] bg-[#EF4444]/15 animate-pulse-gold' : 'border-white/10 text-white/40'}`}>
          Conservative
        </button>
      </div>

      <button data-testid="briefing-compare-btn" onClick={() => narrateCompare()}
        className="w-full px-2 py-1.5 border border-gold/40 text-gold/80 text-[10px] font-mono uppercase tracking-widest text-center transition-colors duration-300 pointer-events-auto hover:bg-gold hover:text-black hover:border-gold">
        ⇄ A/B Compare Both Banks
      </button>

      {/* Progress bar */}
      <div className="w-full h-[2px] bg-white/10 relative">
        <div className="absolute left-0 top-0 h-full bg-gold transition-all duration-700" data-testid="tour-progress" style={{ width: `${progress}%` }} />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-2">
        <button data-testid="prev-zone-btn" onClick={prevZone} disabled={currentZone === 0}
          className="p-2.5 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-25 disabled:cursor-not-allowed">
          <ChevronLeft className="w-4 h-4" />
        </button>

        {tourActive ? (
          <button data-testid="stop-tour-btn" onClick={stopTour}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gold/60 bg-black/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300 uppercase tracking-widest text-[10px] font-bold">
            <Square className="w-3.5 h-3.5" /> End Tour
          </button>
        ) : (
          <button data-testid="start-tour-btn" onClick={startTour}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gold/60 bg-black/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300 uppercase tracking-widest text-[10px] font-bold">
            <Play className="w-3.5 h-3.5" /> Guided Tour
          </button>
        )}

        <button data-testid="replay-zone-btn" onClick={replayZone}
          className="p-2.5 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors duration-300">
          <RotateCcw className="w-4 h-4" />
        </button>

        <button data-testid="next-zone-btn" onClick={nextZone} disabled={currentZone === ZONES.length - 1}
          className="p-2.5 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-25 disabled:cursor-not-allowed">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
